import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Award, Sparkles, Trophy } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import api from "../lib/api.js";

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function Badges() {
  const nav = useNavigate();
  const [badges, setBadges] = useState([]);

  useEffect(() => {
    api.get("/badges").then((r) => setBadges(r.data));
  }, []);

  // group by year
  const byYear = badges.reduce((acc, b) => {
    const y = b.year ?? new Date(b.awarded_at).getFullYear();
    acc[y] = acc[y] || [];
    acc[y].push(b);
    return acc;
  }, {});
  const years = Object.keys(byYear).sort((a, b) => b - a);

  return (
    <PhoneFrame>
      <div className="px-5 pt-6 pb-10">
        <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="badges-back">
          <ChevronLeft size={16} /> Back
        </button>
        <div className="mt-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#142534] flex items-center justify-center">
            <Award size={22} color="#F59E0B" />
          </div>
          <div>
            <h1 className="ps-heading text-2xl font-bold">Badges</h1>
            <p className="text-slate-400 text-sm">Milestones you've crossed.</p>
          </div>
        </div>

        {badges.length === 0 ? (
          <div className="mt-12 text-center text-slate-500">
            <Trophy size={28} className="mx-auto opacity-50" />
            <div className="mt-3 text-sm">No badges yet. Walk to unlock your first one.</div>
            <Link to="/annual-goal" className="ps-button-primary inline-block mt-5 text-sm py-2 px-5">View Annual Goal</Link>
          </div>
        ) : (
          years.map((y) => (
            <div key={y} className="mt-6">
              <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">{y} Milestones</div>
              <div className="grid grid-cols-2 gap-3">
                {byYear[y].sort((a, b) => a.milestone_pct - b.milestone_pct).map((b) => (
                  <div key={b.id} className="ps-card p-4 text-center" data-testid={`badge-${b.code}`}>
                    <div
                      className="w-16 h-16 rounded-full mx-auto flex items-center justify-center text-xl font-bold ps-heading"
                      style={{ background: b.color, color: "#0a1822", boxShadow: `0 0 24px ${b.color}55` }}
                    >
                      {b.milestone_pct >= 100 ? "★" : `${b.milestone_pct}%`}
                    </div>
                    <div className="ps-heading font-semibold mt-3 text-sm">{b.title}</div>
                    <div className="text-[10px] text-slate-400 mt-1 inline-flex items-center gap-1">
                      <Sparkles size={9} /> {relTime(b.awarded_at)}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">
                      {b.steps_at_award?.toLocaleString()} / {b.target_steps?.toLocaleString()} steps
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </PhoneFrame>
  );
}
